// Card resumen del tab Servicios (cliente 360): zonas semánticas
// header/content/footer, layout a dos columnas por container query con
// fallback apilado, y feedback sutil de hover/focus. Toda la card abre el
// detalle; el modal conserva las acciones de edición/eliminación.
import { useState, type CSSProperties } from 'react';
import { Card, Typography, theme } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { StatusTag } from '../../../components/StatusTag';
import { ServicioDetailModal } from '../../servicios/components/ServicioDetailModal';
import { tonoEstadoServicio } from '../cliente-servicios';
import { formatCurrency } from '../../../lib/format';
import { useT } from '../../../locales';
import { parseDateOnly } from '../../../lib/date';
import type { EstadoServicioKey, ServicioResponse } from '@aid/shared';

const FECHA_FORMATO = 'DD/MM/YYYY';
const { Text } = Typography;

/**
 * Badge override for lifecycle service states: colors reuse the existing
 * tonoEstadoServicio mapping (BAJA/BAJA_AL_CORTE → error tone); only the
 * label is fixed. Unlisted keys keep the catalog value.
 */
const BADGE_ESTADOS: Partial<Record<EstadoServicioKey, string>> = {
  BAJA: 'pages.clientes.servicios.badgeBaja',
  BAJA_AL_CORTE: 'pages.clientes.servicios.badgeBajaAlCorte',
};

interface ServicioCardProps {
  servicio: ServicioResponse;
}

function valorTipo(s: ServicioResponse): string {
  return s.tipoServicio?.nombre ?? `#${s.idTipoServicio}`;
}

function valorPlan(s: ServicioResponse): string {
  return s.plan?.nombre ?? `#${s.idPlan}`;
}

function valorPrecio(s: ServicioResponse): string | null {
  return s.plan?.precio ? formatCurrency(s.plan.precio) : null;
}

function valorUbicacion(s: ServicioResponse): string | null {
  const partes = [s.municipio?.value, s.departamento?.value].filter((value): value is string =>
    Boolean(value),
  );
  return partes.length > 0 ? partes.join(', ') : null;
}

function valorDireccion(s: ServicioResponse): string | null {
  return s.direccion?.trim() || null;
}

function valorPeriodo(s: ServicioResponse, formatoDesde: (fecha: string) => string): string | null {
  if (!s.fechaInicio) return null;
  // Date-only contract: parse component-wise (legacy ISO-midnight safe).
  const inicio = parseDateOnly(s.fechaInicio);
  return inicio?.isValid() ? formatoDesde(inicio.format(FECHA_FORMATO)) : null;
}

function esTeclaEspacio(key: string): boolean {
  return key === ' ' || key === 'Spacebar';
}

export function ServicioCard({ servicio }: ServicioCardProps) {
  const t = useT();
  const { token } = theme.useToken();
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const claveEstado = servicio.estado?.key?.toLowerCase() ?? 'sin-estado';
  const estadoKeyMayus = servicio.estado?.key?.toUpperCase() as EstadoServicioKey | undefined;
  const badgeKey = estadoKeyMayus !== undefined ? BADGE_ESTADOS[estadoKeyMayus] : undefined;
  const etiquetaEstado = badgeKey
    ? t(badgeKey)
    : (servicio.estado?.value ?? servicio.estado?.key ?? 'N/A');
  // A fully deactivated service is shown dimmed so it reads as inactive
  // without hiding it (the user opted into the BAJA view to see it).
  const atenuado = estadoKeyMayus === 'BAJA';
  const tipo = valorTipo(servicio);
  const plan = valorPlan(servicio);
  const precio = valorPrecio(servicio);
  const ubicacion = valorUbicacion(servicio);
  const direccion = valorDireccion(servicio);
  const periodo = valorPeriodo(servicio, (fecha) => t('pages.clientes.servicios.desde', { fecha }));
  const titulo = `${t('pages.clientes.servicios.titulo', { id: servicio.id })} · ${tipo}`;
  const ariaLabel = t('pages.clientes.servicios.abrirDetalle', {
    id: servicio.id,
    tipo,
  });

  // Tokens Kanagawa expuestos como custom properties para que los estados
  // de hover/focus (solo alcanzables desde CSS) sigan al tema.
  const cardVars = {
    '--servicio-card-border': token.colorBorderSecondary,
    '--servicio-card-border-hover': token.colorPrimaryBorder,
    '--servicio-card-bg': token.colorBgContainer,
    '--servicio-card-bg-hover': token.colorFillTertiary,
    '--servicio-card-accent': token.colorPrimary,
  } as CSSProperties;

  const abrirDetalle = () => setDetalleId(servicio.id);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={abrirDetalle}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            abrirDetalle();
          }
          if (esTeclaEspacio(event.key)) {
            event.preventDefault();
          }
        }}
        onKeyUp={(event) => {
          if (esTeclaEspacio(event.key)) {
            event.preventDefault();
            abrirDetalle();
          }
        }}
        className="servicio-card-trigger"
        style={cardVars}
      >
        <Card
          size="small"
          className="servicio-card"
          style={{
            borderRadius: token.borderRadiusLG,
            border: '1px solid var(--servicio-card-border)',
            backgroundColor: 'var(--servicio-card-bg)',
            boxShadow: token.boxShadowTertiary,
            transition: 'border-color 150ms ease, background-color 150ms ease',
            opacity: atenuado ? 0.6 : 1,
          }}
          styles={{
            body: { padding: '16px 18px' },
          }}
        >
          <div className="servicio-card__header">
            <Text
              strong
              style={{
                fontSize: 14,
                lineHeight: 1.5,
                color: token.colorText,
                textWrap: 'balance',
              }}
            >
              {titulo}
            </Text>
            <StatusTag tone={tonoEstadoServicio(claveEstado)}>{etiquetaEstado}</StatusTag>
          </div>

          <div className="servicio-card__content">
            <div className="servicio-card__info">
              <Text
                strong
                style={{
                  fontSize: 18,
                  lineHeight: 1.3,
                  color: token.colorText,
                  textWrap: 'pretty',
                }}
              >
                {plan}
              </Text>

              {(ubicacion || direccion) && (
                <span className="servicio-card__ubicacion">
                  <EnvironmentOutlined aria-hidden="true" />
                  <Text
                    type="secondary"
                    style={{ fontSize: 13, lineHeight: 1.4, overflowWrap: 'anywhere' }}
                  >
                    {ubicacion ?? direccion}
                  </Text>
                </span>
              )}
              {direccion && ubicacion && (
                <Text
                  style={{
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: token.colorTextTertiary,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {direccion}
                </Text>
              )}
              {servicio.referencia && (
                <Text
                  style={{
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: token.colorTextTertiary,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {servicio.referencia}
                </Text>
              )}
            </div>

            {precio && (
              <div className="servicio-card__precio">
                <Text
                  strong
                  style={{
                    fontSize: 20,
                    lineHeight: 1.2,
                    color: token.colorText,
                    fontVariantNumeric: 'tabular-nums',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {precio}
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t('pages.servicios.porMes')}
                </Text>
              </div>
            )}
          </div>

          <div className="servicio-card__footer">
            {periodo && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                {periodo}
              </Text>
            )}
            <Text
              aria-hidden="true"
              style={{
                display: 'inline-block',
                color: token.colorPrimary,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {t('pages.clientes.servicios.verDetalles')}
              <span className="servicio-card__flecha" aria-hidden="true">
                {' '}
                →
              </span>
            </Text>
          </div>
        </Card>
      </div>
      {detalleId !== null && (
        <ServicioDetailModal id={detalleId} onClose={() => setDetalleId(null)} />
      )}
    </>
  );
}
